/**
 * Шит найма (03.10, владелец: «ОО нанимает только операторов; ДК, ОО и ИО — только ИО»;
 * «доступ новому — одноразовой ссылкой в STAFF-бота»). Поля: имя и ник — TextPad (M.6),
 * телефон — PhoneInput+PhonePad (M.3-Т), роль — Dropdown (M.4; у ОО роль одна — без
 * выбора), пол — две кнопки. Деньги, Telegram ID и права в форму не входят: оплата —
 * Грейды/Ставки, вход — ссылкой. Футер — ActionPair «Нанять»/«Отмена».
 * 409 сервера (такой человек уже есть по телефону) — плашка «Уже есть: …» с «Вернуть»
 * (уволенного, если сервер сказал can_restore — ОО не возвращает ДК/ОО/ИО) или «Показать»
 * (работающего) вместо второй строки; отказ возврата — текстом в шите, форма цела. Неполный
 * телефон не уходит молча: поле красное с подсказкой, «Нанять» его не отправит (ревью 03.10). После найма шит
 * показывает карточку ссылки — её отправляют человеку здесь же. Пока идёт запрос,
 * шит не закрывается. Сброс формы — при открытии (поля не мигают на выезде).
 */
import { useEffect, useRef, useState } from "react";
import { ActionPair, CheckIco, Dropdown, PhoneInput, PhonePad, SheetDialog, TextPad, actionPairCss as ap } from "../../components/shared";
import { STAFF_ROLE_LABELS, sortRoles, type StaffRoleId } from "@ub/shared/staffRoles";
import { fmtDay, phoneState, type Duplicate, type HireInput, type HireResult, type Invite } from "../../hooks/staffApi";
import { InviteCard } from "./InviteCard";
import css from "./hireSheet.module.css";

interface Props {
  open: boolean;
  /** Роли, которые этот человек вправе выдать (сервер проверяет то же). */
  roleOptions: StaffRoleId[];
  /** ИО выдаёт несколько ролей сразу. */
  multiRole: boolean;
  onClose: () => void;
  hire: (input: HireInput) => Promise<HireResult>;
  /** Вернуть уволенного из плашки 409: успех — ростер закроет шит и раскроет строку; отказ — текст ошибки. */
  onRestore: (d: Duplicate) => Promise<string | null>;
  onShow: (id: string) => void;
  isBound: (id: string) => boolean;
  newInvite: (id: string) => Promise<Invite | null>;
  onPoll: () => void;
}

type Pad = "name" | "nick" | null;

export function HireSheet(p: Props) {
  const [name, setName] = useState("");
  const [nick, setNick] = useState("");
  const [phone, setPhone] = useState("");
  const [roles, setRoles] = useState<StaffRoleId[]>([]);
  const [gender, setGender] = useState<"м" | "ж" | null>(null);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);   // 15.10: двойной тап «Нанять» слал два запроса — второй получал «уже есть»
  const [tried, setTried] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dup, setDup] = useState<Duplicate | null>(null);
  const [done, setDone] = useState<{ id: string; name: string; invite: Invite | null } | null>(null);
  const [pad, setPad] = useState<Pad>(null);
  const [tel, setTel] = useState<HTMLElement | null>(null);
  const [dd, setDd] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!p.open) return;
    setName(""); setNick(""); setPhone(""); setGender(null);
    setRoles(p.roleOptions.includes("operator") ? ["operator"] : p.roleOptions.slice(0, 1));
    setBusy(false); setTried(false); setErr(null); setDup(null); setDone(null); setPad(null); setTel(null); setDd(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.open]);

  const dismiss = () => { if (!busy) p.onClose(); };
  const phoneBad = phoneState(phone) === "short";
  const valid = name.trim().length > 0 && roles.length > 0 && !phoneBad;
  const submit = async () => {
    setTried(true);
    if (!valid || busy || sending.current) return;
    sending.current = true;
    setBusy(true); setErr(null); setDup(null);
    const r = await p.hire({ name, nickname: nick, phone, gender, roles });
    sending.current = false;
    setBusy(false);
    if (r.ok) setDone({ id: r.id, name: name.trim(), invite: r.invite });
    else if (r.existing) setDup(r.existing);
    else setErr(r.error);
  };
  const back = async (d: Duplicate) => {
    setBusy(true); setErr(null);
    const e = await p.onRestore(d);
    setBusy(false);
    if (e) setErr(e);
  };
  /* 15.10 (Вика Б): первое «Нанять» прошло, но ответ со ссылкой до кабинета не дошёл — выдаём ссылку здесь же */
  const inviteDup = async (d: Duplicate) => {
    setBusy(true); setErr(null);
    const inv = await p.newInvite(d.id);
    setBusy(false);
    if (inv) { setDup(null); setDone({ id: d.id, name: name.trim() || d.name, invite: inv }); }
  };
  const reissue = async () => {
    if (!done) return;
    setBusy(true);
    const inv = await p.newInvite(done.id);
    setBusy(false);
    if (inv) setDone({ ...done, invite: inv });
  };

  const roleLabel = sortRoles(roles).map((r) => STAFF_ROLE_LABELS[r]).join(", ");
  return (
    <>
      <SheetDialog open={p.open} onDismiss={dismiss} ariaLabel="Нанять сотрудника" maxHeight="85dvh">
        <div className={css.head}><span className={css.title}>{done ? "Принят" : "Нанять"}</span></div>
        {done ? (
          <div className={css.done}>
            <div className={css.doneT}>{done.name} в ростере{done.invite ? ". Отправьте ссылку для входа:" : "."}</div>
            {done.invite ? (
              <InviteCard invite={done.invite} name={done.name} bound={p.isBound(done.id)} busy={busy} onNew={() => void reissue()} onPoll={p.onPoll} />
            ) : <div className={css.hint}>Telegram уже привязан — вход открыт.</div>}
            <div className={css.doneFoot}>
              <button type="button" className={`${ap.abtn} ${ap.cf} ${ap.solo}`} onClick={p.onClose}><CheckIco />Готово</button>
            </div>
          </div>
        ) : (
          <>
            <div className={css.body}>
              <div className={`${css.field} ${css.full}`}>
                <span className={css.lbl}>Имя и фамилия</span>
                <button type="button" className={`${css.inp} ${name ? "" : css.inpEmpty} ${tried && !name.trim() ? css.inpBad : ""}`} onClick={() => setPad("name")}
                  aria-label={`Имя и фамилия: ${name || "не указано, обязательно"}`}>
                  {name || "обязательно"}
                </button>
              </div>
              <div className={css.field}>
                <span className={css.lbl}>Ник</span>
                <button type="button" className={`${css.inp} ${nick ? "" : css.inpEmpty}`} onClick={() => setPad("nick")}
                  aria-label={`Ник: ${nick || "не указан"}`}>{nick || "для доски"}</button>
              </div>
              <div className={css.field}>
                <span className={css.lbl}>Телефон</span>
                <PhoneInput value={phone} onChange={setPhone} readonly padHost onTap={(e) => setTel(e.currentTarget)} ariaLabel="Телефон сотрудника" hasError={phoneBad} />
                {phoneBad ? <span className={css.err}>Неполный номер — допишите или сотрите</span> : null}
              </div>
              <div className={css.field}>
                <span className={css.lbl}>Роль</span>
                {p.roleOptions.length > 1 ? (
                  <button type="button" className={`${css.inp} ${roles.length ? "" : css.inpEmpty}`} onClick={(e) => setDd(e.currentTarget)}
                    aria-label={`Роль: ${roleLabel || "не выбрана"}`}>{roleLabel || "выбрать"}</button>
                ) : <span className={`${css.inp} ${css.inpFixed}`}>{roleLabel}</span>}
              </div>
              <div className={css.field}>
                <span className={css.lbl}>Пол</span>
                <div className={css.seg}>
                  {(["м", "ж"] as const).map((g) => (
                    <button key={g} type="button" aria-pressed={gender === g} className={`${css.segBtn} ${gender === g ? css.segOn : ""}`}
                      onClick={() => setGender((v) => (v === g ? null : g))}>{g === "м" ? "М" : "Ж"}</button>
                  ))}
                </div>
              </div>
              {dup ? (
                <div className={css.dup}>
                  <span className={css.dupTxt}>
                    Уже есть: <b>{dup.name}</b>{dup.phone ? ` ${dup.phone}` : ""} · {dup.status === "fired" ? `уволен ${fmtDay(dup.firedAt)}` : dup.status === "vacation" ? "в отпуске" : "работает"}
                    {dup.status !== "fired" && justHired(dup.hiredAt) ? <> · нанят(а) только что, в {dup.hiredAt!.slice(11, 16)}. Похоже, первое «Нанять» прошло, а ссылка до вас не дошла — выдайте её кнопкой</> : null}
                  </span>
                  {dup.status !== "fired" && justHired(dup.hiredAt)
                    ? <button type="button" className={`${ap.abtn} ${ap.cf} ${ap.solo}`} disabled={busy} onClick={() => void inviteDup(dup)}>{busy ? "Готовим…" : "Ссылка для входа"}</button>
                    : null}
                  {dup.status !== "fired"
                    ? <button type="button" className={`${ap.abtn} ${ap.cl} ${ap.solo}`} onClick={() => p.onShow(dup.id)}>Показать</button>
                    : dup.canRestore
                      ? <button type="button" className={`${ap.abtn} ${ap.cf} ${ap.solo}`} disabled={busy} onClick={() => void back(dup)}>{busy ? "Возвращаем…" : "Вернуть"}</button>
                      : <span className={css.hint}>Вернуть может только EO</span>}
                </div>
              ) : null}
              {err ? <div className={css.err}>{err}</div> : null}
            </div>
            <div className={css.foot}>
              <ActionPair okLabel={busy ? "Нанимаем…" : "Нанять"} okDisabled={busy || (tried && !valid)} onOk={() => void submit()}
                onCancel={dismiss} cancelDisabled={busy} />
            </div>
          </>
        )}
      </SheetDialog>
      <TextPad open={pad !== null} label={pad === "nick" ? "Ник" : "Имя и фамилия"} value={pad === "nick" ? nick : name}
        maxLength={pad === "nick" ? 40 : 80} placeholder={pad === "nick" ? "Как подписать на доске" : "Имя Фамилия"}
        onCommit={(v) => (pad === "nick" ? setNick(v.trim()) : setName(v.trim()))} onClose={() => setPad(null)} />
      <PhonePad open={!!tel} anchor={tel} label="Телефон" value={phone} onCommit={setPhone} onClose={() => setTel(null)} />
      <Dropdown open={!!dd} anchor={dd} multi={p.multiRole} multiDefault={p.multiRole} title="Роль" selectedIds={roles}
        items={p.roleOptions.map((r) => ({ id: r, label: STAFF_ROLE_LABELS[r] }))}
        onCommit={(ids) => { const next = sortRoles(ids); if (next.length) setRoles(next); }} onClose={() => setDd(null)} />
    </>
  );
}

/** Нанят(а) в последние 30 минут (hiredAt — по Оренбургу, «YYYY-MM-DD HH:MM:SS»): скорее всего, это первое нажатие «Нанять». */
function justHired(hiredAt: string | null): boolean {
  const t = hiredAt ? Date.parse(hiredAt.replace(" ", "T") + "+05:00") : NaN;
  return Number.isFinite(t) && Date.now() - t < 30 * 60_000 && Date.now() - t > -60_000;
}
